'use client';

import { c as _c } from "react/compiler-runtime";
import { Tab } from '@common/components';
import { useSearchParams } from 'next/navigation';
import { type CSSProperties, Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/_components/DonationLayout/DonationSubHeader';
import { useScrollToSection } from '@/app/donation/_hooks/useScrollToSection';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { TABS, TABS_LABELS } from '../constants';
import type { Tab as TabType } from '../types';
interface GiftDetailTabProps {
  className?: string;
  style?: CSSProperties;
  productNumber: string;
  disabledTabs?: TabType[];
}
const MOBILE_STICKY_OFFSET = 48;
const DESKTOP_STICKY_OFFSET = DONATION_DESKTOP_STICKY_TOP;
const GiftDetailTabComponent = t0 => {
  const $ = _c(20);
  const {
    className,
    style,
    disabledTabs
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const {
    scrollToSectionWithTab
  } = useScrollToSection();
  const searchParams = useSearchParams();
  const [tab, setTab] = useState(TABS.BASIC);
  const isUserScrolling = useRef(true);
  let t1;
  let t2;
  if ($[0] !== isDesktopViewport || $[1] !== scrollToSectionWithTab || $[2] !== searchParams) {
    t1 = () => {
      if (searchParams?.get("tab")) {
        scrollToSectionWithTab(searchParams.get("tab") as TabType, {
          behavior: "instant",
          offset: isDesktopViewport ? DESKTOP_STICKY_OFFSET : MOBILE_STICKY_OFFSET
        });
      }
    };
    t2 = [isDesktopViewport, scrollToSectionWithTab, searchParams];
    $[0] = isDesktopViewport;
    $[1] = scrollToSectionWithTab;
    $[2] = searchParams;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== disabledTabs) {
    t3 = () => {
      const tabIds = Object.values(TABS).filter(id => !disabledTabs?.includes(id));
      const observer = new IntersectionObserver(entries => {
        if (!isUserScrolling.current) {
          return;
        }
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const activeId = tabIds.find(id_0 => id_0 === entry.target.id);
            if (activeId) {
              setTab(activeId);
            }
          }
        }
      }, {
        rootMargin: "0px 0px -50% 0px",
        threshold: 0
      });
      tabIds.forEach(id_1 => {
        const element = document.getElementById(id_1);
        if (element) {
          observer.observe(element);
        }
      });
      return () => observer.disconnect();
    };
    t4 = [disabledTabs];
    $[5] = disabledTabs;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const t5 = isDesktopViewport ? "lg" : "sm";
  const t6 = tab ?? TABS.BASIC;
  let t7;
  if ($[8] !== isDesktopViewport || $[9] !== scrollToSectionWithTab) {
    t7 = value => {
      isUserScrolling.current = false;
      setTab(value);
      scrollToSectionWithTab(value, {
        behavior: "instant",
        offset: isDesktopViewport ? DESKTOP_STICKY_OFFSET : MOBILE_STICKY_OFFSET
      });
      setTimeout(() => {
        isUserScrolling.current = true;
      }, 1000);
    };
    $[8] = isDesktopViewport;
    $[9] = scrollToSectionWithTab;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== disabledTabs) {
    t8 = Object.values(TABS).filter(tab_0 => !disabledTabs?.includes(tab_0)).map(_temp);
    $[11] = disabledTabs;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== className || $[14] !== style || $[15] !== t5 || $[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t9 = <Tab size={t5} className={className} style={style} variant="full" value={t6} onChange={t7}>{t8}</Tab>;
    $[13] = className;
    $[14] = style;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
export const GiftDetailTab = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ErrorBoundary fallback={null}><Suspense><GiftDetailTabComponent {...props} /></Suspense></ErrorBoundary>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
function _temp(tab_1) {
  return <Tab.Item key={tab_1} value={tab_1}>{TABS_LABELS[tab_1]}</Tab.Item>;
}